import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Search, ArrowDownLeft, ArrowUpRight, Landmark } from "lucide-react";
import { API, authHeaders, money, inputCls } from "./shared";

const SOURCES = [
  { key: "all", label: "Everything" },
  { key: "online", label: "Online Bookings" },
  { key: "manual", label: "Manual Revenue" },
  { key: "refunds", label: "Refunds" },
  { key: "expenses", label: "Expenses" },
  { key: "deposits", label: "Deposits" },
];

export default function TransactionsLedger({ token, onAuthError, options }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");
  const [method, setMethod] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (source !== "all") params.set("source", source);
    if (method) params.set("method", method);
    if (q) params.set("q", q);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    axios.get(`${API}/finance/transactions?${params}`, authHeaders(token))
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [source, method, q, from, to]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const t = data?.totals || { in: 0, out: 0, net: 0 };

  return (
    <div className="space-y-4" data-testid="transactions-ledger">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#0C0C0C] border border-green-500/25 rounded-xl p-3" data-testid="tx-total-in">
          <div className="text-white/45 text-[11px] uppercase tracking-wider">Money In</div>
          <div className="font-display font-black text-xl text-green-400 mt-0.5">{money(t.in)}</div>
          {t.linked_not_counted > 0 && (
            <div data-testid="tx-linked-total" className="text-[10px] text-white/40 mt-0.5">
              {money(t.linked_not_counted)} in linked duplicates excluded — 1 real dollar = 1 dollar counted
            </div>
          )}
        </div>
        <div className="bg-[#0C0C0C] border border-red-500/25 rounded-xl p-3" data-testid="tx-total-out">
          <div className="text-white/45 text-[11px] uppercase tracking-wider">Money Out</div>
          <div className="font-display font-black text-xl text-red-400 mt-0.5">{money(t.out)}</div>
        </div>
        <div className="bg-[#0C0C0C] border border-[#F2B705]/30 rounded-xl p-3" data-testid="tx-total-net">
          <div className="text-white/45 text-[11px] uppercase tracking-wider">Net</div>
          <div className={`font-display font-black text-xl mt-0.5 ${t.net >= 0 ? "text-[#F2B705]" : "text-red-400"}`}>{money(t.net)}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {SOURCES.map((s) => (
          <button key={s.key} data-testid={`tx-filter-${s.key}`} onClick={() => setSource(s.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
              source === s.key ? "bg-white text-black" : "bg-[#0C0C0C] border border-white/10 text-white/60 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35" />
          <input data-testid="tx-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, vehicle, #…"
            className="pl-8 pr-3 py-1.5 rounded-md bg-[#0C0C0C] border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]/60 w-56" />
        </div>
        <select data-testid="tx-method-filter" value={method} onChange={(e) => setMethod(e.target.value)} className="px-2 py-1.5 rounded-md bg-[#0C0C0C] border border-white/15 text-white text-xs outline-none">
          <option value="">All Methods</option>
          {(options?.payment_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input data-testid="tx-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} !w-auto !py-1.5 !text-xs`} />
        <span className="text-white/30 text-xs">to</span>
        <input data-testid="tx-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputCls} !w-auto !py-1.5 !text-xs`} />
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#F2B705]" /></div> : (
        <div className="bg-[#0C0C0C] border border-white/10 rounded-xl overflow-hidden">
          <div className="hidden md:grid grid-cols-[90px_1fr_1fr_120px_110px_90px] gap-2 px-4 py-2 text-[11px] uppercase tracking-wider text-white/35 border-b border-white/10">
            <span>Date</span><span>Transaction</span><span>Customer / Vehicle</span><span>Method</span><span className="text-right">Amount</span><span className="text-right">Deposit</span>
          </div>
          {(data?.entries || []).length === 0 && <p className="text-white/35 text-sm p-5">No transactions match these filters.</p>}
          <div className="divide-y divide-white/5 max-h-[560px] overflow-y-auto">
            {(data?.entries || []).map((e) => (
              <div key={e.ref} data-testid={`tx-row-${e.ref}`} className={`grid grid-cols-2 md:grid-cols-[90px_1fr_1fr_120px_110px_90px] gap-2 px-4 py-2.5 text-sm items-center ${e.voided ? "opacity-45" : ""}`}>
                <span className="text-white/50 text-xs">{e.date || "—"}</span>
                <span className="min-w-0">
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    {e.direction === "in" && <ArrowDownLeft size={13} className="text-green-400 shrink-0" />}
                    {e.direction === "out" && <ArrowUpRight size={13} className="text-red-400 shrink-0" />}
                    {e.direction === "transfer" && <Landmark size={13} className="text-blue-400 shrink-0" />}
                    <span className="text-white/85">{e.type}</span>
                    {e.number && <span className="text-white/35 text-xs">{e.number}</span>}
                    {e.is_manual && e.source === "Manual Revenue" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F2B705]/15 text-[#F2B705] font-semibold">Manual Revenue Entry</span>}
                    {e.source === "Online Booking" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 font-semibold">Online Booking</span>}
                    {e.source === "Refund" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 font-semibold">Refund</span>}
                    {e.voided && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold">VOIDED</span>}
                    {e.counted === false && (
                      <span data-testid={`tx-linked-${e.ref}`} title={e.link_note || "Same payment as the processor transaction — allocation only"}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50 font-semibold">
                        🔗 Linked — not counted
                      </span>
                    )}
                    {e.possible_duplicate && (
                      <span data-testid={`tx-possible-dup-${e.ref}`} title={e.link_note}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 font-semibold">
                        ⚠ Possible duplicate — review
                      </span>
                    )}
                  </span>
                </span>
                <span className="text-white/60 text-xs truncate">{e.customer}{e.vehicle ? ` · ${e.vehicle}` : ""}</span>
                <span className="text-white/50 text-xs">{e.method}</span>
                <span className={`text-right font-semibold ${e.direction === "out" ? "text-red-400" : e.direction === "transfer" ? "text-blue-300" : "text-green-400"}`}>
                  {e.direction === "out" ? "−" : ""}{money(e.amount)}
                </span>
                <span className="text-right text-xs text-white/40">{e.deposit_number || (e.direction === "in" ? "—" : "")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
